"use strict";
/**
 * Dependency-free rehype plugin used to sanitize the HTML tree produced
 * by `@uiw/react-markdown-preview`. The library enables `rehype-raw` by
 * default, which would otherwise render arbitrary `<script>`/`<iframe>`/
 * event-handler attributes from user-supplied content.
 *
 * Strips:
 *  - a denylist of dangerous element tag names,
 *  - all `on*` event handler props,
 *  - unsafe URL schemes (`javascript:` / `vbscript:` / `data:` / `file:`)
 *    on href/src/action/poster attributes.
 *
 * Defense-in-depth alongside backend validation.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.rehypeSanitizeMarkdown = void 0;
var REHYPE_BLOCKED_TAG_NAMES = new Set([
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'noembed',
    'noscript',
    'link',
    'meta',
    'base',
    'form',
    'input',
    'button',
    'textarea',
    'select',
    'option',
    'frame',
    'frameset',
    'applet',
    'audio',
    'video',
    'source',
    'track',
]);
var REHYPE_URL_ATTRIBUTES = new Set([
    'href',
    'src',
    'action',
    'formaction',
    'poster',
    'background',
    'xlinkHref',
    'xlink:href',
]);
var REHYPE_UNSAFE_URL_SCHEME = /^\s*(javascript|vbscript|data|file):/i;
var isEventHandlerAttribute = function (name) {
    return name.length > 2 && name.slice(0, 2).toLowerCase() === 'on';
};
var isUnsafeUrlValue = function (value) {
    return typeof value === 'string' && REHYPE_UNSAFE_URL_SCHEME.test(value);
};
var sanitizeRehypeProperties = function (properties) {
    if (!properties) {
        return properties;
    }
    var cleaned = {};
    for (var _i = 0, _a = Object.keys(properties); _i < _a.length; _i++) {
        var key = _a[_i];
        if (isEventHandlerAttribute(key)) {
            continue;
        }
        var value = properties[key];
        if (REHYPE_URL_ATTRIBUTES.has(key) && isUnsafeUrlValue(value)) {
            continue;
        }
        cleaned[key] = value;
    }
    return cleaned;
};
var sanitizeRehypeChildren = function (children) {
    if (!Array.isArray(children) || children.length === 0) {
        return [];
    }
    var result = [];
    for (var _i = 0, children_1 = children; _i < children_1.length; _i++) {
        var child = children_1[_i];
        if (child &&
            child.type === 'element' &&
            typeof child.tagName === 'string' &&
            REHYPE_BLOCKED_TAG_NAMES.has(child.tagName.toLowerCase())) {
            continue;
        }
        if (child && child.type === 'element') {
            child.properties = sanitizeRehypeProperties(child.properties);
            child.children = sanitizeRehypeChildren(child.children);
        }
        else if (child && Array.isArray(child.children)) {
            child.children = sanitizeRehypeChildren(child.children);
        }
        result.push(child);
    }
    return result;
};
/**
 * Rehype plugin used to sanitize the HTML tree produced by markdown rendering.
 * Pass via `rehypePlugins={[rehypeSanitizeMarkdown]}` on `MarkdownPreview`.
 */
exports.rehypeSanitizeMarkdown = function () {
    return function (tree) {
        if (!tree) {
            return;
        }
        tree.children = sanitizeRehypeChildren(tree.children);
    };
};
